import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CreditCard, FileText, Link2, MailPlus, Plus, Printer } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmt, QuoteLines, StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NEXT_ACTION = {
  draft: { label: "Confirm Booking", to: "confirmed", testid: "booking-action-confirm" },
  confirmed: { label: "Dispatch Units", to: "dispatched", testid: "booking-action-dispatch" },
  dispatched: { label: "Mark Completed", to: "completed", testid: "booking-action-complete" },
};

export default function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: "", method: "bank_transfer", note: "" });
  const [paying, setPaying] = useState(false);
  const [menus, setMenus] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [staffOpen, setStaffOpen] = useState(false);
  const [staffForm, setStaffForm] = useState({ staff_id: "", shift_role: "", start_time: "", end_time: "" });
  const [equipOpen, setEquipOpen] = useState(false);
  const [equipForm, setEquipForm] = useState({ equipment_id: "", quantity: 1 });
  const [itemForm, setItemForm] = useState({ description: "", amount: "" });

  const load = () => api.get(`/bookings/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  const loadEquipment = () => api.get("/equipment").then((r) => setEquipmentList(r.data.filter((e) => !e.archived))).catch(() => {});
  useEffect(() => {
    load();
    loadEquipment();
    api.get("/menus").then((r) => setMenus(r.data.filter((m) => !m.archived))).catch(() => {});
    api.get("/staff").then((r) => setStaffList(r.data.filter((s) => !s.archived))).catch(() => {});
  }, [id]);

  if (!data) return <div data-testid="booking-loading" className="py-20 text-center text-sm text-muted-foreground">Loading booking…</div>;

  const action = NEXT_ACTION[data.status];
  const customSum = (data.custom_items || []).reduce((s, i) => s + (i.amount || 0), 0);
  const ledgerQuote = data.quote
    ? { ...data.quote, total: (data.quote.total || 0) + customSum, deposit: (data.quote.deposit || 0) + customSum * 0.25 }
    : data.quote;
  const balance = data.balance ?? Math.max(0, (ledgerQuote?.total || 0) - (data.amount_paid || 0));

  const saveCustomItems = async (items) => {
    try {
      await api.put(`/bookings/${id}/custom-items`, { items });
      toast.success("Custom items saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setStatus = async (status) => {
    try {
      await api.post(`/bookings/${id}/status`, { status });
      toast.success(`Booking ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const payDeposit = async () => {
    setPaying(true);
    try {
      const { data: res } = await api.post("/payments/checkout", { booking_id: id, origin_url: window.location.origin });
      localStorage.setItem("pending_booking", id);
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setPaying(false);
    }
  };

  const recordPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/bookings/${id}/payments`, { amount: Number(payForm.amount), method: payForm.method, note: payForm.note });
      toast.success("Payment recorded");
      setPayOpen(false);
      setPayForm({ amount: "", method: "bank_transfer", note: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const assignMenu = async (menuId) => {
    try {
      await api.post(`/bookings/${id}/menu`, { menu_id: menuId || null });
      toast.success("Menu assigned — quote & production sheet updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleKitchen = async (taskId, done) => {
    try {
      await api.patch(`/bookings/${id}/kitchen/${taskId}`, { done });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addStaff = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/bookings/${id}/staff`, staffForm);
      toast.success("Shift assigned");
      setStaffOpen(false);
      setStaffForm({ staff_id: "", shift_role: "", start_time: "", end_time: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeStaff = async (aid) => {
    try {
      await api.delete(`/bookings/${id}/staff/${aid}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addEquipment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/bookings/${id}/equipment`, { ...equipForm, quantity: Number(equipForm.quantity) });
      toast.success("Equipment assigned");
      setEquipOpen(false);
      setEquipForm({ equipment_id: "", quantity: 1 });
      load();
      loadEquipment();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeEquipment = async (aid) => {
    try {
      await api.delete(`/bookings/${id}/equipment/${aid}`);
      load();
      loadEquipment();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const genInvoice = async () => {
    try {
      const { data: inv } = await api.post(`/bookings/${id}/invoices`);
      toast.success(`Invoice ${inv.invoice_number} issued`);
      load();
      navigate(`/invoices/${inv.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyPortal = () => {
    navigator.clipboard.writeText(`${window.location.origin}/portal/${data.portal_token}`);
    toast.success("Client portal link copied");
  };

  const sendRfq = async (providerId) => {
    try {
      await api.post(`/bookings/${id}/rfqs`, { provider_id: providerId, note: "Availability request" });
      toast.success("RFQ logged & marked as sent");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setRfqStatus = async (rfqId, status) => {
    try {
      await api.patch(`/bookings/${id}/rfqs/${rfqId}`, { status });
      toast.success(`RFQ ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="booking-detail-page" className="space-y-6">
      <button data-testid="back-to-bookings" onClick={() => navigate("/bookings")} className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors duration-150 hover:text-ink">
        <ArrowLeft className="h-4 w-4" /> All bookings
      </button>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <h1 className="font-heading font-mono text-3xl font-bold tracking-tight" data-testid="booking-ref">{data.booking_ref}</h1>
          <StatusBadge status={data.status} />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="event-sheet-button" variant="outline" onClick={() => navigate(`/bookings/${id}/sheet`)} className="rounded-md">
            <Printer className="mr-1.5 h-4 w-4" /> Event Sheet
          </Button>
          <Button data-testid="portal-link-button" variant="outline" onClick={copyPortal} className="rounded-md">
            <Link2 className="mr-1.5 h-4 w-4" /> Client Portal Link
          </Button>
          {action && (
            <Button data-testid={action.testid} onClick={() => setStatus(action.to)} className="rounded-md bg-brand hover:bg-brand-dark">{action.label}</Button>
          )}
          {!["cancelled", "completed"].includes(data.status) && (
            <Button data-testid="booking-action-cancel" variant="outline" onClick={() => setStatus("cancelled")} className="rounded-md border-red-300 text-red-600 hover:bg-red-50">Cancel</Button>
          )}
        </div>
      </div>

      {data.status === "draft" && !data.deposit_paid && data.deposit_due_at && (
        <div className="flex items-center justify-between border border-amber-300 bg-amber-50 px-5 py-3" data-testid="deposit-countdown-banner">
          <p className="text-sm font-semibold text-amber-900">
            Deposit of {fmt(data.quote?.deposit || 0)} due by {data.deposit_due_at.slice(0, 10)} — the date is only held until then
            {(() => { const h = Math.max(0, Math.round((new Date(data.deposit_due_at) - Date.now()) / 36e5)); return h > 0 ? ` (${h}h left)` : " (expired)"; })()}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <div className="border border-border bg-white p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Event</p>
            <dl className="mt-4 grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Customer</dt><dd className="font-medium" data-testid="booking-customer">{data.customer_name}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Event date</dt><dd className="font-medium tabular-nums" data-testid="booking-date">{data.event_date}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Guests</dt><dd className="font-medium tabular-nums">{data.guest_count}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Venue</dt><dd className="font-medium">{data.venue_name || "TBC"}</dd></div>
            </dl>
            {data.venue && (
              <div className="mt-4 border-t border-border pt-4">
                <div className="flex flex-wrap gap-1.5">
                  {(data.venue.site_rules || []).map((r) => (
                    <span key={r} className="bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800" data-testid={`booking-venue-rule-${r.slice(0, 8)}`}>{r}</span>
                  ))}
                </div>
                {data.venue.lat != null && <p className="mt-2 font-mono text-[11px] text-muted-foreground">GPS {Number(data.venue.lat).toFixed(4)}, {Number(data.venue.lng).toFixed(4)}</p>}
              </div>
            )}
          </div>

          <div className="border border-border bg-white p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Assigned mobile units</p>
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
              {data.units.map((u) => (
                <div key={u.id} data-testid={`booking-unit-${u.id}`} className="flex items-center justify-between border border-border px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold">{u.name}</p>
                    <p className="text-xs text-muted-foreground">{u.capacity} covers · {u.unit_type}</p>
                  </div>
                  <StatusBadge status={u.status} />
                </div>
              ))}
              {data.units.length === 0 && <p className="text-sm text-muted-foreground">No units assigned.</p>}
            </div>
          </div>

          <div className="border border-border bg-white p-6" data-testid="rfq-section">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">External provider dispatch</p>
            <div className="mt-4 space-y-3">
              {data.providers.map((p) => {
                const rfq = (data.rfqs || []).find((r) => r.provider_id === p.id);
                return (
                  <div key={p.id} data-testid={`booking-provider-${p.id}`} className="flex flex-wrap items-center justify-between gap-3 border border-border px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold">{p.name}</p>
                      <p className="text-xs text-muted-foreground capitalize">{p.category} · {fmt(p.rate)}/day · {p.lead_time_days}d lead</p>
                    </div>
                    {rfq ? (
                      <div className="flex items-center gap-2">
                        <StatusBadge status={rfq.status} />
                        {rfq.status === "sent" && (
                          <>
                            <Button data-testid={`rfq-confirm-${rfq.id}`} size="sm" variant="outline" className="rounded-md border-emerald-400 text-emerald-700 hover:bg-emerald-50" onClick={() => setRfqStatus(rfq.id, "confirmed")}>Confirm</Button>
                            <Button data-testid={`rfq-decline-${rfq.id}`} size="sm" variant="outline" className="rounded-md border-red-300 text-red-600 hover:bg-red-50" onClick={() => setRfqStatus(rfq.id, "declined")}>Decline</Button>
                          </>
                        )}
                      </div>
                    ) : (
                      <Button data-testid={`send-rfq-${p.id}`} size="sm" variant="outline" className="rounded-md" onClick={() => sendRfq(p.id)}>
                        <MailPlus className="mr-1.5 h-3.5 w-3.5" /> Send RFQ
                      </Button>
                    )}
                  </div>
                );
              })}
              {data.providers.length === 0 && <p className="text-sm text-muted-foreground">No external providers assigned to this event.</p>}
            </div>
          </div>

          <div className="border border-border bg-white p-6" data-testid="menu-production-section">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Menu &amp; kitchen production</p>
              {!data.payments?.length && (
                <div className="w-56">
                  <Select value={data.menu_id || ""} onValueChange={assignMenu}>
                    <SelectTrigger data-testid="menu-assign-select"><SelectValue placeholder="Assign menu…" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {menus.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`menu-option-${m.id}`}>{m.name} — {fmt(m.price_per_head)}/head</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {data.menu_name && <p className="mt-3 text-sm font-semibold" data-testid="booking-menu-name">{data.menu_name}</p>}
            {(() => {
              const menu = menus.find((m) => m.id === data.menu_id);
              const items = (menu?.items || []).filter((it) => (it.allergens || []).length > 0);
              if (!menu || items.length === 0) return null;
              const allAllergens = [...new Set(items.flatMap((it) => it.allergens))];
              return (
                <div className="mt-4 border-t border-border pt-4" data-testid="allergen-matrix">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Allergen matrix</p>
                    <Button data-testid="allergen-print-button" size="sm" variant="outline" className="rounded-md" onClick={() => window.print()}><Printer className="mr-1.5 h-3.5 w-3.5" /> Print</Button>
                  </div>
                  <div className="overflow-x-auto">
                  <table className="mt-3 w-full text-xs">
                    <thead>
                      <tr className="border-b border-border text-left">
                        <th className="py-1.5 pr-2 font-semibold">Dish</th>
                        {allAllergens.map((a) => <th key={a} className="px-1 py-1.5 text-center font-semibold capitalize">{a}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it, i) => (
                        <tr key={i} className="border-b border-border last:border-0" data-testid={`allergen-row-${i}`}>
                          <td className="py-1.5 pr-2 font-medium">{it.name}</td>
                          {allAllergens.map((a) => (
                            <td key={a} className="px-1 py-1.5 text-center">{it.allergens.includes(a) ? <span className="font-black text-red-600">●</span> : <span className="text-muted-foreground">—</span>}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              );
            })()}
            <div className="mt-3 space-y-2">
              {(data.kitchen_tasks || []).map((t) => (
                <label key={t.id} data-testid={`kitchen-task-${t.id}`} className={`flex cursor-pointer items-center gap-3 border border-border px-3 py-2 text-sm transition-colors duration-150 hover:bg-slate-50 ${t.done ? "opacity-50" : ""}`}>
                  <Checkbox checked={t.done} onCheckedChange={(v) => toggleKitchen(t.id, !!v)} data-testid={`kitchen-checkbox-${t.id}`} />
                  <span className={`flex-1 font-medium ${t.done ? "line-through" : ""}`}>{t.name}</span>
                  <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{t.course} · {t.portion}</span>
                  <span className="text-xs font-bold tabular-nums">× {t.quantity}</span>
                </label>
              ))}
              {(data.kitchen_tasks || []).length === 0 && <p className="text-sm text-muted-foreground">Assign a menu to generate the kitchen production list.</p>}
            </div>
          </div>

          <div className="border border-border bg-white p-6" data-testid="staff-rota-section">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Staff rota</p>
              <Button data-testid="assign-staff-button" size="sm" variant="outline" className="rounded-md" onClick={() => setStaffOpen(true)}>
                <Plus className="mr-1 h-3.5 w-3.5" /> Assign Shift
              </Button>
            </div>
            <div className="mt-4 space-y-2">
              {(data.staff_assignments || []).map((s) => (
                <div key={s.id} data-testid={`staff-assignment-${s.id}`} className="flex items-center justify-between border border-border px-4 py-2.5 text-sm">
                  <div>
                    <p className="font-medium">{s.name}</p>
                    <p className="text-xs text-muted-foreground">{s.role} · {s.start_time || "—"}–{s.end_time || "—"} · {fmt(s.day_rate)}</p>
                  </div>
                  <button data-testid={`remove-staff-${s.id}`} onClick={() => removeStaff(s.id)} className="text-xs font-semibold text-red-600 hover:underline">Remove</button>
                </div>
              ))}
              {(data.staff_assignments || []).length === 0 && <p className="text-sm text-muted-foreground">No crew assigned yet.</p>}
            </div>
          </div>

          <div className="border border-border bg-white p-6" data-testid="equipment-section">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Event equipment</p>
              <Button data-testid="assign-equipment-button" size="sm" variant="outline" className="rounded-md" onClick={() => setEquipOpen(true)}>
                <Plus className="mr-1 h-3.5 w-3.5" /> Assign Equipment
              </Button>
            </div>
            <div className="mt-4 space-y-2">
              {(data.equipment_assignments || []).map((e) => (
                <div key={e.id} data-testid={`equipment-assignment-${e.id}`} className="flex items-center justify-between border border-border px-4 py-2.5 text-sm">
                  <p className="font-medium">{e.name} <span className="text-muted-foreground">× {e.quantity}</span></p>
                  <button data-testid={`remove-equipment-${e.id}`} onClick={() => removeEquipment(e.id)} className="text-xs font-semibold text-red-600 hover:underline">Remove</button>
                </div>
              ))}
              {(data.equipment_assignments || []).length === 0 && <p className="text-sm text-muted-foreground">No equipment assigned yet.</p>}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="border border-border bg-white p-6" data-testid="financial-ledger">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Financial ledger</p>
            <div className="mt-4"><QuoteLines quote={ledgerQuote} /></div>

            <div className="mt-4" data-testid="custom-items-editor">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Custom items</p>
              {(data.custom_items || []).length === 0 && <p className="mt-2 text-sm text-muted-foreground">No custom items. Add extras like additional staff, equipment hire, or one-off charges.</p>}
              {(data.custom_items || []).map((item, i) => (
                <div key={i} className="mt-2 flex items-center justify-between gap-2 border border-border px-3 py-2 text-sm" data-testid={`custom-item-${i}`}>
                  <span className="min-w-0">{item.description}</span>
                  <span className="flex items-center gap-3">
                    <span className="font-semibold tabular-nums">{fmt(item.amount)}</span>
                    <button type="button" data-testid={`custom-item-del-${i}`} onClick={() => saveCustomItems((data.custom_items || []).filter((_, j) => j !== i))} className="text-muted-foreground transition-colors duration-150 hover:text-red-600" title="Remove">×</button>
                  </span>
                </div>
              ))}
              <div className="mt-2 flex flex-wrap gap-2">
                <Input placeholder="Description (e.g. Extra staff member)" className="min-w-0 basis-full sm:basis-auto sm:flex-1" data-testid="custom-item-desc" value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} />
                <Input placeholder="£" type="number" min="0" step="0.01" className="w-28" data-testid="custom-item-amount" value={itemForm.amount} onChange={(e) => setItemForm({ ...itemForm, amount: e.target.value })} />
                <Button type="button" size="sm" variant="outline" className="rounded-md" data-testid="custom-item-add" disabled={!itemForm.description.trim() || !itemForm.amount}
                  onClick={() => { saveCustomItems([...(data.custom_items || []), { description: itemForm.description.trim(), amount: Number(itemForm.amount) }]); setItemForm({ description: "", amount: "" }); }}>
                  Add
                </Button>
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">Included in the balance, deposit calculation and invoice lines.</p>
            </div>

            <div className="mt-4 border border-border">
              <div className="border-b border-border bg-muted/50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Payments received</div>
              {(data.payments || []).length === 0 && <p className="px-4 py-3 text-sm text-muted-foreground">No payments recorded.</p>}
              {(data.payments || []).map((p, i) => (
                <div key={i} data-testid={`payment-entry-${i}`} className="flex items-center justify-between border-b border-border px-4 py-2.5 text-sm last:border-0">
                  <div>
                    <p className="font-medium capitalize">{p.payment_type} · {p.method}</p>
                    {p.note && <p className="text-xs text-muted-foreground">{p.note}</p>}
                  </div>
                  <span className="font-semibold text-brand tabular-nums">{fmt(p.amount)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between bg-muted/50 px-4 py-2.5 text-sm">
                <span className="font-semibold">Balance remaining</span>
                <span className="font-heading font-bold tabular-nums" data-testid="booking-balance">{fmt(balance)}</span>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {!data.deposit_paid && !["cancelled", "completed"].includes(data.status) && (
                <Button data-testid="pay-deposit-button" onClick={payDeposit} disabled={paying} className="w-full rounded-md bg-brand hover:bg-brand-dark">
                  <CreditCard className="mr-2 h-4 w-4" /> {paying ? "Redirecting…" : `Pay Deposit ${fmt((data.quote?.deposit || 0) - (data.amount_paid || 0))} via Stripe`}
                </Button>
              )}
              <Button data-testid="record-payment-button" variant="outline" onClick={() => setPayOpen(true)} className="w-full rounded-md border-ink text-ink hover:bg-muted">
                <Plus className="mr-2 h-4 w-4" /> Record Manual Payment
              </Button>
            </div>
          </div>

          <div className="border border-border bg-white p-6" data-testid="invoices-section">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Invoices</p>
              <Button data-testid="generate-invoice-button" size="sm" variant="outline" className="rounded-md" onClick={genInvoice}>
                <FileText className="mr-1 h-3.5 w-3.5" /> Issue Invoice
              </Button>
            </div>
            <div className="mt-4 space-y-2">
              {(data.invoices || []).map((inv) => (
                <button key={inv.id} data-testid={`invoice-row-${inv.id}`} onClick={() => navigate(`/invoices/${inv.id}`)}
                  className="flex w-full items-center justify-between border border-border px-4 py-2.5 text-left text-sm transition-colors duration-150 hover:bg-slate-50">
                  <span className="font-mono text-xs font-bold">{inv.invoice_number}</span>
                  <span className="tabular-nums">{fmt(inv.total)}</span>
                  <StatusBadge status={inv.status === "paid" ? "paid" : inv.status === "void" ? "cancelled" : "sent"} />
                </button>
              ))}
              {(data.invoices || []).length === 0 && <p className="text-sm text-muted-foreground">No invoices issued.</p>}
            </div>
          </div>

          <div className="border border-border bg-white p-6" data-testid="booking-timeline">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Timeline</p>
            <div className="mt-4">
              {[...(data.timeline || [])].reverse().map((t, i) => (
                <div key={i} className="relative border-l-2 border-border pb-4 pl-4 last:pb-0">
                  <div className="absolute -left-[5px] top-1 h-2 w-2 rounded-full bg-ink" />
                  <p className="text-sm">{t.event}</p>
                  <p className="text-xs text-muted-foreground">{t.by}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={staffOpen} onOpenChange={setStaffOpen}>
        <DialogContent className="max-w-sm bg-white" data-testid="assign-staff-dialog">
          <DialogHeader><DialogTitle className="font-heading">Assign Shift</DialogTitle></DialogHeader>
          <form onSubmit={addStaff} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Staff member</Label>
              <Select value={staffForm.staff_id} onValueChange={(v) => setStaffForm({ ...staffForm, staff_id: v })}>
                <SelectTrigger data-testid="staff-assign-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {staffList.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} — {s.role}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Shift role (optional override)</Label><Input data-testid="shift-role-input" value={staffForm.shift_role} onChange={(e) => setStaffForm({ ...staffForm, shift_role: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Start</Label><Input data-testid="shift-start-input" type="time" value={staffForm.start_time} onChange={(e) => setStaffForm({ ...staffForm, start_time: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>End</Label><Input data-testid="shift-end-input" type="time" value={staffForm.end_time} onChange={(e) => setStaffForm({ ...staffForm, end_time: e.target.value })} /></div>
            </div>
            <Button data-testid="assign-staff-submit" type="submit" disabled={!staffForm.staff_id} className="w-full rounded-md bg-ink hover:bg-ink-light">Assign</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={equipOpen} onOpenChange={setEquipOpen}>
        <DialogContent className="max-w-sm bg-white" data-testid="assign-equipment-dialog">
          <DialogHeader><DialogTitle className="font-heading">Assign Equipment</DialogTitle></DialogHeader>
          <form onSubmit={addEquipment} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Equipment</Label>
              <Select value={equipForm.equipment_id} onValueChange={(v) => setEquipForm({ ...equipForm, equipment_id: v })}>
                <SelectTrigger data-testid="equipment-assign-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {equipmentList.map((e) => <SelectItem key={e.id} value={e.id} data-testid={`equipment-option-${e.id}`}>{e.name} ({e.available ?? e.quantity} available)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Quantity</Label><Input data-testid="equipment-assign-qty" type="number" min="1" value={equipForm.quantity} onChange={(e) => setEquipForm({ ...equipForm, quantity: e.target.value })} /></div>
            <Button data-testid="assign-equipment-submit" type="submit" disabled={!equipForm.equipment_id} className="w-full rounded-md bg-ink hover:bg-ink-light">Assign</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-sm bg-white" data-testid="record-payment-dialog">
          <DialogHeader><DialogTitle className="font-heading">Record Payment</DialogTitle></DialogHeader>
          <form onSubmit={recordPayment} className="space-y-4">
            <div className="space-y-1.5"><Label>Amount (£)</Label><Input data-testid="payment-amount-input" type="number" step="0.01" min="0.01" required value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Method</Label>
              <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="card">Card (offline)</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Note</Label><Input data-testid="payment-note-input" value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} /></div>
            <Button data-testid="payment-submit-button" type="submit" className="w-full rounded-md bg-ink hover:bg-ink-light">Record Payment</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
